import React, { memo, useEffect, useState } from 'react';
import Highcharts from 'highcharts/esm/highmaps.js';
import { AxisLabelsFormatterContextObject, Point } from 'highcharts/highmaps';
import 'highcharts/modules/boost';
import CustomEvents from 'highcharts-custom-events';
import HighchartsReact from 'highcharts-react-official';
import { FormatNumberForChart } from '@components-clean/core/charts/shared/number-utils';
import { CHART_HEIGHT_PIXELS, getCreditsStyle, getChartEvents } from '@components-clean/core/charts/shared/utils';
import Loading from '@components-clean/core/loading/loading';
import Tooltip from '@components-clean/core/tooltip';
import { HC_COLORS_STANDARD, IOpraChartKey, OpraColours, OpraVerticalAlign } from '@interfaces/opra-chart.interface';
import { SeriesDataHeatMap, SeriesDataHeatMapV2 } from '@interfaces-clean/panels.interface';
import { useTranslation } from '@hooks/index';

CustomEvents(Highcharts);

export interface HeatMapChartProps {
    xAxisTitle?: string;
    yAxisTitle?: string;
    series: SeriesDataHeatMap | SeriesDataHeatMapV2;
    secondaryKey?: IOpraChartKey;
    showLegend?: boolean;
    plotTitle?: string;
    hidePlotTitle?: boolean;
    chartRef?: React.RefObject<HighchartsReact.RefObject | null>;
    noDataMessage?: string;
    showWaterMark?: boolean;
    version: number;
}

const styleHeight = { height: '350px' };

const HeatMapChartComponent = (props: HeatMapChartProps) => {
    const {
        xAxisTitle,
        yAxisTitle,
        series,
        secondaryKey,
        showLegend,
        plotTitle,
        hidePlotTitle,
        chartRef,
        noDataMessage,
        showWaterMark = false,
        version,
    } = props;
    const [hasData, setHasData] = useState(true);
    const [open, setOpen] = useState<boolean>(false);
    const [targetElement, setTargetElement] = useState<EventTarget | null>(null);
    const [options, setOptions] = useState<HighchartsReact.Props | null>(null);
    const { t } = useTranslation();

    const xAxisCategories: string[] = [];
    const yAxisCategories: string[] = [];
    const getData = () => {
        if (version === 1) {
            const { order, heatmapForIds } = series as SeriesDataHeatMap;
            yAxisCategories.push(...(order || []));
            return Object.keys(heatmapForIds).flatMap((key) => {
                const value = heatmapForIds[key];
                return value.map((x) => {
                    if (!xAxisCategories.includes(x.x)) {
                        xAxisCategories.push(x.x);
                    }
                    if (!yAxisCategories.includes(x.yName)) {
                        yAxisCategories.push(x.yName);
                    }

                    return [xAxisCategories.indexOf(x.x), yAxisCategories.indexOf(x.yName), x.value];
                });
            });
        }

        const { data, xValues, yValues } = series as SeriesDataHeatMapV2;
        xAxisCategories.push(...xValues!);
        yAxisCategories.push(...yValues!);
        return data!;
    };

    useEffect(() => {
        const data: number[][] = getData();
        setHasData(data.length > 0);
        const yAxisLabels: string[] = [...yAxisCategories].map((item: string) => item);
        const dataArrayLength: number = yAxisCategories.length * xAxisCategories.length;

        if (version === 1 && data.length !== dataArrayLength)
            throw Error(`Expecting ${dataArrayLength} data points but found ${data.length}`);

        setOptions({
            credits: showWaterMark ? getCreditsStyle(`${t('cm:waterMarkMainPanelText')}`) : { enabled: false },
            chart: {
                type: 'heatmap',
                zoomType: 'xy',
                height: CHART_HEIGHT_PIXELS,
                events: getChartEvents(showWaterMark, false, plotTitle, undefined, {
                    printOptions: {
                        chart: {
                            height: 600,
                            width: 600,
                        },
                    },
                    defaultOptions: {
                        chart: {
                            height: CHART_HEIGHT_PIXELS,
                            width: null,
                        },
                    },
                }),
            },
            exporting: {
                chartOptions: {
                    title: { text: plotTitle, style: { fontWeight: 'semi-bold' } },
                    credits: { enabled: false },
                },
                enabled: !chartRef,
                fallbackToExportServer: false,
                buttons: {
                    contextButton: {
                        enabled: false,
                    },
                },
            },
            subtitle:
                secondaryKey === undefined
                    ? undefined
                    : {
                          text: secondaryKey.text,
                          useHTML: true,
                          verticalAlign:
                              secondaryKey.position === OpraVerticalAlign.top
                                  ? 'top'
                                  : secondaryKey.position === OpraVerticalAlign.middle
                                    ? 'middle'
                                    : 'bottom',
                          style: { display: (showLegend ?? true) ? undefined : 'none' },
                      },
            legend: {
                enabled: showLegend,
                itemStyle: {
                    color: HC_COLORS_STANDARD[OpraColours.DarkGrey],
                    cursor: 'pointer',
                    fontWeight: 'normal',
                    fontSize: '12px',
                    textOverflow: 'ellipsis',
                },
            },
            xAxis: {
                categories: [...xAxisCategories],
                title: {
                    text: xAxisTitle,
                },
                labels: {
                    style: {
                        textOverflow: 'ellipsis',
                    },
                    events: {
                        mouseover(e: MouseEvent) {
                            if (e.target) {
                                setTargetElement(e.target);
                                setOpen(true);
                            }
                        },
                        mouseout() {
                            setTargetElement(null);
                            setOpen(false);
                        },
                    },
                },
            },
            yAxis: {
                categories: [...yAxisCategories],
                title: {
                    text: yAxisTitle,
                },
                labels: {
                    formatter: (event: AxisLabelsFormatterContextObject) => yAxisLabels[event.pos],
                    allowOverlap: false,
                    padding: 3,
                },
                tickInterval: 1,
                max: yAxisCategories.length,
                scrollbar: {
                    enabled: true,
                },
            },
            colorAxis: {
                stops: [
                    [0, '#ffffff'],
                    [0.1, '#ffffbf'],
                    [0.5, '#f5a623'],
                    [0.8, '#d0021b'],
                    [1, '#d0021b'],
                ],
                min: 0 /* Maybe this should be an option on the panel? -- Math.min(...chartProps?.series?.data) */,
                max: series.maxValue,
            },
            tooltip: {
                formatter() {
                    const point = this as Point;
                    let result = `<b>${point.series.xAxis.categories[point.x]}</b><br/>`;
                    result += `${point.series.yAxis.userOptions.title?.text}: `;
                    if (point.y != null) {
                        result += `${yAxisCategories[point.y]}<br/>`;
                    }
                    result += `Value: ${FormatNumberForChart(point.value as number)}`;
                    return result;
                },
            },
            series: [
                {
                    name: hidePlotTitle ? null : plotTitle,
                    data,
                },
            ],
        });
    }, [props]);

    if (options === null) return <Loading />;

    return hasData || !noDataMessage ? (
        <Tooltip anchorElement="pointer" open={open} targetElement={targetElement} position="left">
            <HighchartsReact ref={chartRef} highcharts={Highcharts} constructorType="chart" options={options} />{' '}
        </Tooltip>
    ) : (
        <div style={styleHeight}>{noDataMessage}</div>
    );
};

export const HeatMapChart = memo(HeatMapChartComponent);
